import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { collections } from '../data/mock';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export const CollectionPage = () => {
  const { slug } = useParams();
  
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  
  // Find the collection based on slug
  const collection = collections.find(c => 
    c.name.toLowerCase().replace(/\s+/g, '-') === slug
  );

  if (!collection) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-4xl font-light text-stone-900">Collection not found</h1>
          <Link to="/" className="text-stone-600 hover:text-stone-900 mt-4 inline-block">
            Return to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const ImageCard = ({ image, index }) => {
    const imageSrc = typeof image === 'string' ? image : image.src;
    const caption = typeof image === 'object' ? image.caption : null;
    const productSlug = typeof image === 'object' ? image.productSlug : null;
    const objectPosition = typeof image === 'object' && image.objectPosition ? image.objectPosition : 'center';

    const imageContent = (
      <>
        <div className={`relative aspect-square bg-stone-100 overflow-hidden ${productSlug ? 'cursor-pointer' : ''}`}>
          <img
            src={imageSrc}
            alt={`${collection.name} ${index + 1}`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            style={{ objectPosition: objectPosition }}
          />
        </div>
        {caption && (
          <p className="text-center font-bold text-stone-900 mt-4">
            {caption}
          </p>
        )}
      </>
    );

    if (productSlug) {
      return (
        <Link to={`/product/${productSlug}`} className="group">
          {imageContent}
        </Link>
      );
    }

    return <div className="group">{imageContent}</div>;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${collection.heroImage || collection.images[0]})` }}
        ></div>
        <div className="absolute inset-0 bg-stone-900/50"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-light tracking-tight text-white mb-6 drop-shadow-lg">
            {collection.name}
          </h1>
          <p className="text-xl text-white max-w-3xl mx-auto font-light leading-relaxed drop-shadow-md">
            {collection.description}
          </p>
        </div>
      </section>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <Link 
          to="/" 
          onClick={() => {
            setTimeout(() => {
              const element = document.getElementById('collections');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          }}
        >
          <Button variant="ghost" className="text-stone-600 hover:text-stone-900">
            <ArrowLeft className="mr-2" size={20} />
            Back to Collections
          </Button>
        </Link>
      </div>

      {/* Gallery Section */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {collection.images.map((image, index) => (
              <ImageCard key={index} image={image} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-stone-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light tracking-tight text-stone-900 mb-6">
            Interested in {collection.name}?
          </h2>
          <p className="text-lg text-stone-600 leading-relaxed mb-8">
            Visit our showrooms to experience our {collection.name.toLowerCase()} collection in person, or reach out to our team for personalized assistance.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/display-centres"
              onClick={() => {
                window.scrollTo(0, 0);
              }}
            >
              <Button className="bg-stone-900 hover:bg-stone-800 text-stone-50 px-10 py-4 text-sm tracking-widest transition-all duration-300">
                VISIT SHOWROOMS
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
